import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SiteHeader from '../components/common/SiteHeader';
import authService from '../services/auth';

function RegisterPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState('');
  const [searchParams] = useSearchParams();
  const MIN_PASSWORD_LENGTH = 8;

  useEffect(() => {
    const errorParam = searchParams.get('error');
    const emailParam = searchParams.get('email');

    if (errorParam) {
      switch (errorParam) {
        case 'AccountNotFound':
          setError(`No account found with email ${emailParam ? decodeURIComponent(emailParam) : 'this email'}. Please sign up to create an account.`);
          if (emailParam) setEmail(decodeURIComponent(emailParam));
          break;
        case 'GoogleAuthFailed':
          setError('Google authentication failed. Please try again.');
          break;
        case 'InvalidGoogleData':
          setError('Invalid data received from Google. Please try again.');
          break;
        default:
          setError('An error occurred during authentication.');
      }
    }
  }, [searchParams]);

  function getPasswordStrength(pw) {
    if (pw.length < 8) return 'Too short';
    if (/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).{12,}$/.test(pw)) return 'Strong';
    if (/^(?=.*[a-zA-Z])(?=.*\d).{8,}$/.test(pw)) return 'Medium';
    return 'Weak';
  }

  function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (!email || !password || !confirmPassword) {
      setError('All fields are required.');
      return;
    }
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters long.`);
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    // Extract name from email if not provided separately (since the form doesn't have a name field)
    const name = email.split('@')[0]; // Use email prefix as name for simplicity

    // Real registration implementation
    authService.register(email, password, name)
      .then(result => {
        if (result.success) {
          setSuccess(true);
          setTimeout(() => {
            // Use the auth service's redirect logic
            authService.handleLoginSuccess();
          }, 1200);
        } else {
          setError(result.error || 'Registration failed. Please try again.');
        }
      })
      .catch(error => {
        setError('Registration failed. Please try again.');
      });
  }

  function handlePasswordChange(e) {
    setPassword(e.target.value);
    setPasswordStrength(getPasswordStrength(e.target.value));
  }

  function handleConfirmChange(e) {
    setConfirmPassword(e.target.value);
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex flex-col">
      <SiteHeader />
      <form onSubmit={handleSubmit} className="w-full max-w-md mx-auto bg-white rounded shadow p-8 mt-12" autoComplete="off">
        <h1 className="text-2xl font-bold mb-6">Register</h1>
        <button
          type="button"
          className="w-full flex items-center justify-center gap-2 mb-4 px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
          onClick={() => authService.initiateGoogleLogin()}
        >
          <img src="https://www.svgrepo.com/show/475656/google-color.svg" alt="Google" className="w-5 h-5" />
          <span className="font-medium text-gray-700">Sign up with Google</span>
        </button>
        <div className="flex items-center my-4">
          <div className="flex-grow h-px bg-gray-200" />
          <span className="mx-2 text-gray-400 text-xs">or</span>
          <div className="flex-grow h-px bg-gray-200" />
        </div>
        {error && <div className="mb-4 text-red-600 text-sm">{error}</div>}
        {success && <div className="mb-4 text-green-600 text-sm">Registration successful! Redirecting...</div>}
        <div className="mb-4">
          <label className="block mb-1 font-medium" htmlFor="register-email">Email</label>
          <input id="register-email" type="email" value={email} onChange={e => setEmail(e.target.value)} required className="w-full border rounded px-3 py-2" autoComplete="email" />
        </div>
        <div className="mb-4">
          <label className="block mb-1 font-medium" htmlFor="register-password">Password</label>
          <div className="relative">
            <input
              id="register-password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={handlePasswordChange}
              required
              minLength={MIN_PASSWORD_LENGTH}
              className="w-full border rounded px-3 py-2 pr-10"
              autoComplete="new-password"
            />
            <button
              type="button"
              tabIndex={-1}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-primary-500 hover:underline"
              onClick={() => setShowPassword(v => !v)}
            >
              {showPassword ? 'Hide' : 'Show'}
            </button>
          </div>
          <span className="text-xs text-gray-500">Minimum {MIN_PASSWORD_LENGTH} characters</span>
          {password && (
            <div className="mt-1 text-xs font-semibold"
              style={{ color: passwordStrength === 'Strong' ? '#059669' : passwordStrength === 'Medium' ? '#f59e42' : '#dc2626' }}>
              Password strength: {passwordStrength}
            </div>
          )}
        </div>
        <div className="mb-6">
          <label className="block mb-1 font-medium" htmlFor="register-confirm">Confirm Password</label>
          <div className="relative">
            <input
              id="register-confirm"
              type={showConfirm ? 'text' : 'password'}
              value={confirmPassword}
              onChange={handleConfirmChange}
              required
              minLength={MIN_PASSWORD_LENGTH}
              className="w-full border rounded px-3 py-2 pr-10"
              autoComplete="new-password"
            />
            <button
              type="button"
              tabIndex={-1}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-primary-500 hover:underline"
              onClick={() => setShowConfirm(v => !v)}
            >
              {showConfirm ? 'Hide' : 'Show'}
            </button>
          </div>
        </div>
        <div className="flex gap-4 items-center">
          <button type="submit" className="px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 font-medium transition-colors flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
            </svg>
            Register
          </button>
          <Link to="/login" className="text-primary-500 hover:underline">Already have an account? Login</Link>
        </div>
      </form>
    </div>
  );
}

export default RegisterPage;
